import { Button } from '../ui/button';

export interface DeleteChannelModalProps {
  /** `null` closes the modal. */
  channel: { id: string; title: string | null } | null;
  /** Set once the server answered 409: how many dialogs are still open. */
  blockedBy: number | null;
  /** Only an owner may force-delete a channel that still has open dialogs. */
  canForce: boolean;
  pending?: boolean;
  onCancel: () => void;
  onConfirm: (force: boolean) => void;
}

/**
 * U5.3 — confirmation for deleting a channel. On `409 channel_has_dialogs` the
 * modal stays open and explains what to do (archive/close the dialogs), rather
 * than showing a bare error: force-deleting is a second, explicit decision.
 */
export function DeleteChannelModal({ channel, blockedBy, canForce, pending, onCancel, onConfirm }: DeleteChannelModalProps) {
  if (!channel) return null;
  const name = channel.title ?? 'канал';

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div role="dialog" aria-label="Удалить канал" className="w-full max-w-md rounded bg-card p-5 shadow-xl">
        <h2 className="mb-3 text-lg font-semibold">Удалить канал «{name}»?</h2>

        {blockedBy === null ? (
          <p className="text-sm text-muted-fg">
            Канал будет отключён и удалён вместе с его настройками и историей проверок. Это действие нельзя отменить.
          </p>
        ) : (
          <div className="space-y-2 text-sm">
            <p role="alert" className="rounded bg-red-50 p-2 text-red-700">
              У канала {blockedBy} незакрытых диалог(ов), поэтому обычное удаление отклонено.
            </p>
            <p className="text-muted-fg">
              Сначала архивируйте или закройте эти диалоги — так переписка сохранится.
              {canForce
                ? ' Либо удалите канал вместе с диалогами и сообщениями: восстановить их будет нельзя.'
                : ' Удалить канал вместе с диалогами может только владелец рабочего пространства.'}
            </p>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Отмена
          </Button>
          {blockedBy === null ? (
            <Button variant="danger" disabled={pending} onClick={() => onConfirm(false)}>
              Удалить
            </Button>
          ) : (
            canForce && (
              <Button variant="danger" disabled={pending} onClick={() => onConfirm(true)}>
                Удалить вместе с диалогами
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}
